///
/// EarthBrowser
///
/// Created by Matt Giger on 1/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import { type DatasetFeature } from "@/earthbrowser/dataset-distribution/DatasetLayer"

export interface SatelliteTLEProperties extends DatasetFeature {
	id: string                    // alias for object_id
	object_id: string
	object_name: string
	epoch: string
	mean_motion: number
	eccentricity: number
	inclination: number
	ra_of_asc_node: number
	arg_of_pericenter: number
	mean_anomaly: number
	norad_cat_id: number
	rev_at_epoch: number
	bstar: number
	mean_motion_dot: number
	mean_motion_ddot: number
	group_ids: string[]           // Array of group IDs this satellite belongs to
}

const cBatchSize = 500

export class SatelliteTLETable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists satellite_tle (
					object_id text primary key,
					object_name text not null,
					epoch text not null,
					mean_motion real not null,
					eccentricity real not null,
					inclination real not null,
					ra_of_asc_node real not null,
					arg_of_pericenter real not null,
					mean_anomaly real not null,
					norad_cat_id integer not null,
					rev_at_epoch integer not null,
					bstar real not null,
					mean_motion_dot real not null,
					mean_motion_ddot real not null,
					group_ids text not null default '[]'
				)`
			},
			{ sql: "create index if not exists idx_satellite_tle_name on satellite_tle(object_name collate nocase)" },
			{ sql: "create index if not exists idx_satellite_tle_norad on satellite_tle(norad_cat_id)" },
			{ sql: "create index if not exists idx_satellite_tle_epoch on satellite_tle(epoch desc)" },
		])
	}

	public static async replaceAll(rows: SatelliteTLEProperties[]): Promise<void> {
		await Database.db.exec("delete from satellite_tle", [], "critical")
		await this.insert(rows)
	}

	public static async insert(rows: SatelliteTLEProperties[]): Promise<void> {
		const statement = `insert into satellite_tle (
				object_id, object_name, epoch,
				mean_motion, eccentricity, inclination, ra_of_asc_node,
				arg_of_pericenter, mean_anomaly, norad_cat_id, rev_at_epoch,
				bstar, mean_motion_dot, mean_motion_ddot, group_ids
			) values (
				?, ?, ?,
				?, ?, ?, ?,
				?, ?, ?, ?,
				?, ?, ?, ?
			)
			on conflict(object_id) do update set
				object_name = excluded.object_name,
				epoch = excluded.epoch,
				mean_motion = excluded.mean_motion,
				eccentricity = excluded.eccentricity,
				inclination = excluded.inclination,
				ra_of_asc_node = excluded.ra_of_asc_node,
				arg_of_pericenter = excluded.arg_of_pericenter,
				mean_anomaly = excluded.mean_anomaly,
				norad_cat_id = excluded.norad_cat_id,
				rev_at_epoch = excluded.rev_at_epoch,
				bstar = excluded.bstar,
				mean_motion_dot = excluded.mean_motion_dot,
				mean_motion_ddot = excluded.mean_motion_ddot,
				group_ids = excluded.group_ids`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(s => [
				s.object_id,
				s.object_name,
				s.epoch,
				s.mean_motion,
				s.eccentricity,
				s.inclination,
				s.ra_of_asc_node,
				s.arg_of_pericenter,
				s.mean_anomaly,
				s.norad_cat_id,
				s.rev_at_epoch,
				s.bstar,
				s.mean_motion_dot,
				s.mean_motion_ddot,
				Array.isArray(s.group_ids) ? JSON.stringify(s.group_ids) : s.group_ids,
			]), "low") // ~24k-row ingest: yield to visible-layer reads (see LocationsTable.insert)
		}
	}

	public static async getAll(): Promise<SatelliteTLEProperties[]> {
		const rows = await Database.db.exec(
			`select
				object_id,
				object_name,
				epoch,
				mean_motion,
				eccentricity,
				inclination,
				ra_of_asc_node,
				arg_of_pericenter,
				mean_anomaly,
				norad_cat_id,
				rev_at_epoch,
				bstar,
				mean_motion_dot,
				mean_motion_ddot,
				group_ids
			from satellite_tle
			order by object_name`,
			[],
			"low"
		) as Array<Omit<SatelliteTLEProperties, 'id' | 'group_ids'> & { group_ids: string }>

		return rows.map(r => ({
			...r,
			id: r.object_id,
			group_ids: typeof r.group_ids === 'string' ? JSON.parse(r.group_ids) as string[] : r.group_ids,
		}))
	}

	/** SQL fragment matching rows in any of the groups, with its parameters */
	private static groupsClause(groupIds: readonly string[]): { sql: string, params: string[] } {
		return {
			sql: `(${groupIds.map(() => "group_ids like ?").join(" or ")})`,
			params: groupIds.map(id => `%"${id}"%`),
		}
	}

	/** Satellites in any of the given groups, each object once */
	public static async getByGroupIds(groupIds: readonly string[]): Promise<SatelliteTLEProperties[]> {
		if (groupIds.length === 0) return []
		const clause = SatelliteTLETable.groupsClause(groupIds)
		const rows = await Database.db.exec(
			`select
				object_id,
				object_name,
				epoch,
				mean_motion,
				eccentricity,
				inclination,
				ra_of_asc_node,
				arg_of_pericenter,
				mean_anomaly,
				norad_cat_id,
				rev_at_epoch,
				bstar,
				mean_motion_dot,
				mean_motion_ddot,
				group_ids
			from satellite_tle
			where ${clause.sql}
			order by object_name`,
			clause.params,
			"low"
		) as Array<Omit<SatelliteTLEProperties, 'id' | 'group_ids'> & { group_ids: string }>

		return rows.map(r => ({
			...r,
			id: r.object_id,
			group_ids: typeof r.group_ids === 'string' ? JSON.parse(r.group_ids) as string[] : r.group_ids,
		}))
	}

	public static async getCountByGroupIds(groupIds: readonly string[]): Promise<number> {
		if (groupIds.length === 0) return 0
		const clause = SatelliteTLETable.groupsClause(groupIds)
		const rows = await Database.db.exec(
			`select count(*) as cnt from satellite_tle where ${clause.sql}`,
			clause.params,
			"low"
		) as Array<{ cnt: number }>

		return Number(rows[0]?.cnt ?? 0)
	}

	public static async getByGroupId(groupId: string): Promise<SatelliteTLEProperties[]> {
		// Use JSON contains to find satellites in a specific group
		const rows = await Database.db.exec(
			`select
				object_id,
				object_name,
				epoch,
				mean_motion,
				eccentricity,
				inclination,
				ra_of_asc_node,
				arg_of_pericenter,
				mean_anomaly,
				norad_cat_id,
				rev_at_epoch,
				bstar,
				mean_motion_dot,
				mean_motion_ddot,
				group_ids
			from satellite_tle
			where group_ids like ?
			order by object_name`,
			[`%"${groupId}"%`],
			"low"
		) as Array<Omit<SatelliteTLEProperties, 'id' | 'group_ids'> & { group_ids: string }>

		return rows.map(r => ({
			...r,
			id: r.object_id,
			group_ids: typeof r.group_ids === 'string' ? JSON.parse(r.group_ids) as string[] : r.group_ids,
		}))
	}

	public static async getByIds(objectIds: string[]): Promise<SatelliteTLEProperties[]> {
		if (objectIds.length === 0) return []
		const placeholders = objectIds.map(() => '?').join(', ')
		const rows = await Database.db.exec(
			`select
				object_id,
				object_name,
				epoch,
				mean_motion,
				eccentricity,
				inclination,
				ra_of_asc_node,
				arg_of_pericenter,
				mean_anomaly,
				norad_cat_id,
				rev_at_epoch,
				bstar,
				mean_motion_dot,
				mean_motion_ddot,
				group_ids
			from satellite_tle
			where object_id in (${placeholders})`,
			objectIds,
			"low"
		) as Array<Omit<SatelliteTLEProperties, 'id' | 'group_ids'> & { group_ids: string }>

		return rows.map(r => ({
			...r,
			id: r.object_id,
			group_ids: typeof r.group_ids === 'string' ? JSON.parse(r.group_ids) as string[] : r.group_ids,
		}))
	}

	public static async getByNoradIds(noradIds: number[]): Promise<SatelliteTLEProperties[]> {
		if (noradIds.length === 0) return []
		const placeholders = noradIds.map(() => '?').join(', ')
		const rows = await Database.db.exec(
			`select
				object_id,
				object_name,
				epoch,
				mean_motion,
				eccentricity,
				inclination,
				ra_of_asc_node,
				arg_of_pericenter,
				mean_anomaly,
				norad_cat_id,
				rev_at_epoch,
				bstar,
				mean_motion_dot,
				mean_motion_ddot,
				group_ids
			from satellite_tle
			where norad_cat_id in (${placeholders})`,
			noradIds,
			"low"
		) as Array<Omit<SatelliteTLEProperties, 'id' | 'group_ids'> & { group_ids: string }>

		return rows.map(r => ({
			...r,
			id: r.object_id,
			group_ids: typeof r.group_ids === 'string' ? JSON.parse(r.group_ids) as string[] : r.group_ids,
		}))
	}

	public static async search(query: string, limit: number = 100): Promise<SatelliteTLEProperties[]> {
		const rows = await Database.db.exec(
			`select
				object_id,
				object_name,
				epoch,
				mean_motion,
				eccentricity,
				inclination,
				ra_of_asc_node,
				arg_of_pericenter,
				mean_anomaly,
				norad_cat_id,
				rev_at_epoch,
				bstar,
				mean_motion_dot,
				mean_motion_ddot,
				group_ids
			from satellite_tle
			where object_name like ?
			order by object_name
			limit ?`,
			[`%${query}%`, limit],
			"low"
		) as Array<Omit<SatelliteTLEProperties, 'id' | 'group_ids'> & { group_ids: string }>

		return rows.map(r => ({
			...r,
			id: r.object_id,
			group_ids: typeof r.group_ids === 'string' ? JSON.parse(r.group_ids) as string[] : r.group_ids,
		}))
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			"select count(*) as cnt from satellite_tle",
			[],
			"low"
		) as Array<{ cnt: number }>

		return Number(rows[0]?.cnt ?? 0)
	}

	public static async getCountByGroupId(groupId: string): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as cnt from satellite_tle where group_ids like ?`,
			[`%"${groupId}"%`],
			"low"
		) as Array<{ cnt: number }>

		return Number(rows[0]?.cnt ?? 0)
	}

	/**
	 * Paginated query with optional search and sort, filtered to the given
	 * groups (any match); no groups means the whole catalog
	 */
	public static async queryPaginated(options: {
		groupIds?: readonly string[]
		search?: string
		sortColumn?: string
		sortDirection?: 'asc' | 'desc'
		offset?: number
		limit?: number
	}): Promise<{ rows: SatelliteTLEProperties[], totalCount: number }> {
		const { groupIds, search, sortColumn, sortDirection = 'asc', offset = 0, limit = 200 } = options

		const conditions: string[] = []
		const params: (string | number)[] = []

		if (groupIds && groupIds.length > 0) {
			const clause = SatelliteTLETable.groupsClause(groupIds)
			conditions.push(clause.sql)
			params.push(...clause.params)
		}

		if (search) {
			conditions.push(`(object_name like ? or cast(norad_cat_id as text) like ?)`)
			params.push(`%${search}%`, `%${search}%`)
		}

		const whereClause = conditions.length > 0 ? `where ${conditions.join(' and ')}` : ''

		const validSortColumns = ['object_name', 'norad_cat_id', 'inclination', 'eccentricity', 'epoch', 'mean_motion']
		const orderColumn = validSortColumns.includes(sortColumn ?? '') ? sortColumn : 'object_name'
		const orderDir = sortDirection === 'desc' ? 'desc' : 'asc'
		const orderClause = `order by ${orderColumn} ${orderDir}`

		const countResult = await Database.db.exec(
			`select count(*) as cnt from satellite_tle ${whereClause}`,
			params,
			"low"
		) as Array<{ cnt: number }>
		const totalCount = Number(countResult[0]?.cnt ?? 0)

		const dataParams = [...params, limit, offset]
		const rows = await Database.db.exec(
			`select
				object_id,
				object_name,
				epoch,
				mean_motion,
				eccentricity,
				inclination,
				ra_of_asc_node,
				arg_of_pericenter,
				mean_anomaly,
				norad_cat_id,
				rev_at_epoch,
				bstar,
				mean_motion_dot,
				mean_motion_ddot,
				group_ids
			from satellite_tle
			${whereClause}
			${orderClause}
			limit ? offset ?`,
			dataParams,
			"low"
		) as Array<Omit<SatelliteTLEProperties, 'id' | 'group_ids'> & { group_ids: string }>

		return {
			rows: rows.map(r => ({
				...r,
				id: r.object_id,
				group_ids: typeof r.group_ids === 'string' ? JSON.parse(r.group_ids) as string[] : r.group_ids,
			})),
			totalCount
		}
	}
}

